import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  CheckSquare,
  Clock3,
  Gauge,
  Loader2,
  MessageCircle,
  PackageCheck,
  RefreshCw,
  Search,
  Send,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { useMemo, useState } from "react";
import { AdminState } from "../components/admin/AdminState";
import { SettingsPanel } from "../components/admin/SettingsPanel";
import { StatusBadge } from "../components/admin/StatusBadge";
import {
  adminPost,
  buildAdminQuery,
  type AdminMetric,
  type AdminOrderCandidate,
  type AdminReviewRequest,
  useAdminQuery,
} from "../components/admin/adminApi";

type RequestsResponse = {
  ok: true;
  orders: AdminOrderCandidate[];
  requests: AdminReviewRequest[];
  summary: AdminMetric[];
  timing: {
    afterDeliveryDays: number;
    afterFulfillmentDays: number;
    backfillWindowDays: number;
    batchSize: number;
    fallbackOrderLookbackDays: number;
    fallbackToFulfillment: boolean;
    fallbackToOrderDate: boolean;
    tokenExpiryDays: number;
  };
  total: number;
};

type SendOrdersResponse = {
  ok: true;
  results: Array<{ ok: boolean; orderNumber?: string; reason?: string }>;
  sentCount: number;
};

type CancelUnsafeResponse = {
  bullJobsActive: number;
  bullJobsRemoved: number;
  items: number;
  messages: number;
  ok: true;
  outboxJobs: number;
  requests: number;
};

const requestStatuses = ["all", "PENDING", "QUEUED", "SENT", "DELIVERED", "READ", "SUBMITTED", "FAILED", "EXPIRED"];

export function ReviewRequestsPage() {
  const [orderSearch, setOrderSearch] = useState("");
  const [search, setSearch] = useState("");
  const [selectedOrderIds, setSelectedOrderIds] = useState<string[]>([]);
  const [status, setStatus] = useState("all");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ tone: "critical" | "success"; text: string }>();
  const query = useMemo(
    () => buildAdminQuery("/api/admin/review-requests", { orderSearch, search, status }),
    [orderSearch, search, status],
  );
  const requestQuery = useAdminQuery<RequestsResponse>(query, (data) => data.requests.length === 0 && data.orders.length === 0);
  const orders = requestQuery.data?.orders ?? [];
  const requests = requestQuery.data?.requests ?? [];
  const timing = requestQuery.data?.timing;
  const selectableOrderIds = orders.filter(isSelectableOrder).map((order) => order.id);
  const selectedReadyCount = selectedOrderIds.filter((id) => selectableOrderIds.includes(id)).length;
  const groupedRequests = useMemo(() => groupRequests(requests), [requests]);

  async function createTestRequest() {
    setBusy(true);
    setNotice(undefined);
    try {
      await adminPost("/api/admin/review-requests/test", {});
      setNotice({ tone: "success", text: "Test request created without sending a live customer message." });
      await requestQuery.reload();
    } catch (error) {
      setNotice({
        tone: "critical",
        text: error instanceof Error ? error.message : "Could not create test request.",
      });
    } finally {
      setBusy(false);
    }
  }

  async function sendSelectedOrders() {
    const safeSelection = selectedOrderIds.filter((id) => selectableOrderIds.includes(id));
    if (safeSelection.length === 0) return;
    setBusy(true);
    setNotice(undefined);
    try {
      const response = await adminPost<SendOrdersResponse>("/api/admin/review-requests/send-orders", { orderIds: safeSelection });
      const failed = response.results.length - response.sentCount;
      setNotice({
        tone: response.sentCount > 0 ? "success" : "critical",
        text:
          failed > 0
            ? `${response.sentCount} delivered-order requests queued. ${failed} skipped by safety checks.`
            : `${response.sentCount} delivered-order requests queued for WhatsApp/email sending.`,
      });
      setSelectedOrderIds([]);
      await requestQuery.reload();
    } catch (error) {
      setNotice({
        tone: "critical",
        text: error instanceof Error ? error.message : "Selected orders could not be sent.",
      });
    } finally {
      setBusy(false);
    }
  }

  async function cancelUnsafeQueued() {
    setBusy(true);
    setNotice(undefined);
    try {
      const response = await adminPost<CancelUnsafeResponse>("/api/admin/review-requests/cancel-unsafe", {});
      const activeText = response.bullJobsActive > 0 ? ` ${response.bullJobsActive} worker jobs were already active.` : "";
      setNotice({
        tone: "success",
        text: `Safety audit cancelled ${response.requests} requests, ${response.messages} messages, ${response.outboxJobs} outbox jobs, and removed ${response.bullJobsRemoved} queued worker jobs.${activeText}`,
      });
      setSelectedOrderIds([]);
      await requestQuery.reload();
    } catch (error) {
      setNotice({
        tone: "critical",
        text: error instanceof Error ? error.message : "Unsafe queued requests could not be cancelled.",
      });
    } finally {
      setBusy(false);
    }
  }

  function toggleOrder(order: AdminOrderCandidate) {
    if (!isSelectableOrder(order)) return;
    setSelectedOrderIds((current) =>
      current.includes(order.id) ? current.filter((id) => id !== order.id) : [...current, order.id],
    );
  }

  function toggleVisibleOrders() {
    const allSelected = selectableOrderIds.length > 0 && selectableOrderIds.every((id) => selectedOrderIds.includes(id));
    setSelectedOrderIds(
      allSelected
        ? selectedOrderIds.filter((id) => !selectableOrderIds.includes(id))
        : [...new Set([...selectedOrderIds, ...selectableOrderIds])],
    );
  }

  return (
    <div className="wr-page wr-requests-page">
      <section className="wr-requests-hero">
        <div className="wr-requests-hero__content">
          <span className="wr-hero-kicker">
            <ShieldCheck size={15} aria-hidden />
            Verified buyer collection
          </span>
          <h1>Delivered backfill control room</h1>
          <p>
            Send WhatsApp-first review requests to safe delivered orders. Automation still waits for the delivery delay; searched
            delivered orders can be sent manually.
          </p>
          <div className="wr-hero-signal-row" aria-label="Current review request signal">
            <span>{orders.filter((order) => order.eligible).length} auto ready</span>
            <span>{orders.filter((order) => order.manualEligible && !order.eligible).length} manual ready</span>
            <span>{requests.filter((row) => row.rawStatus === "QUEUED" || row.rawStatus === "PENDING").length} queued</span>
            <span>{requests.filter((row) => row.rawStatus === "SUBMITTED").length} submitted</span>
          </div>
        </div>
        <div className="wr-hero-actions">
          <button className="wr-button wr-button--secondary" type="button" onClick={() => void requestQuery.reload()}>
            <RefreshCw size={15} aria-hidden />
            Refresh
          </button>
          <button className="wr-button wr-button--secondary" type="button" disabled={busy} onClick={() => void createTestRequest()}>
            {busy ? <Loader2 className="wr-spin" size={15} aria-hidden /> : <Sparkles size={15} aria-hidden />}
            Test request
          </button>
          <button
            className="wr-button wr-button--dark"
            type="button"
            disabled={busy || selectedReadyCount === 0}
            onClick={() => void sendSelectedOrders()}
          >
            {busy ? <Loader2 className="wr-spin" size={15} aria-hidden /> : <Send size={15} aria-hidden />}
            Send selected
          </button>
        </div>
      </section>

      {notice ? <div className={`wr-notice wr-notice--${notice.tone}`}>{notice.text}</div> : null}

      {requestQuery.data?.summary.length ? (
        <section className="wr-kpi-strip" aria-label="Review request lifecycle summary">
          {requestQuery.data.summary.map((metric) => (
            <RequestMetricCard key={metric.label} metric={metric} />
          ))}
        </section>
      ) : null}

      <section className="wr-backfill-layout">
        <section className="wr-panel wr-backfill-panel">
          <div className="wr-section-heading">
            <div>
              <h2>60-day delivered patch queue</h2>
              <p>Choose the exact delivered orders you want to send in this patch.</p>
            </div>
            <div className="wr-selection-counter">
              <strong>{selectedReadyCount}</strong>
              <span>selected</span>
            </div>
          </div>

          <div className="wr-order-toolbar wr-order-toolbar--premium">
            <label>
              <span>Find an order</span>
              <div className="wr-input-with-icon">
                <Search size={15} aria-hidden />
                <input
                  aria-label="Search orders"
                  placeholder="Search H51266, #H51266, or customer"
                  value={orderSearch}
                  onChange={(event) => setOrderSearch(event.target.value)}
                />
              </div>
            </label>
            <button className="wr-button wr-button--secondary" type="button" onClick={toggleVisibleOrders}>
              <CheckSquare size={15} aria-hidden />
              {selectableOrderIds.length > 0 && selectableOrderIds.every((id) => selectedOrderIds.includes(id))
                ? "Clear ready"
                : "Select ready"}
            </button>
          </div>

          <AdminState
            status={requestQuery.status}
            error={requestQuery.error}
            onRetry={() => void requestQuery.reload()}
            emptyTitle="No delivered orders are ready"
            emptyText="The default queue only shows delivered orders older than the delay and inside the active backfill window. Search a specific order to inspect why it is not eligible."
          >
            <div className="wr-order-list wr-order-list--premium">
              {orders.map((order) => (
                <OrderCandidateCard
                  key={order.id}
                  order={order}
                  selected={selectedOrderIds.includes(order.id)}
                  onToggle={() => toggleOrder(order)}
                />
              ))}
            </div>
          </AdminState>
        </section>

        <aside className="wr-policy-rail" aria-label="Review request safety policy">
          <section className="wr-policy-card">
            <div className="wr-policy-card__header">
              <Gauge size={18} aria-hidden />
              <div>
                <h2>Patch rules</h2>
                <p>Applied before any message is queued.</p>
              </div>
            </div>
            <dl className="wr-policy-list">
              <div>
                <dt>Delivery age</dt>
                <dd>{timing ? `${timing.afterDeliveryDays}+ days` : "14+ days"}</dd>
              </div>
              <div>
                <dt>Lookback</dt>
                <dd>{timing ? `${timing.backfillWindowDays} days` : "60 days"}</dd>
              </div>
              <div>
                <dt>Patch size</dt>
                <dd>{timing ? `${timing.batchSize} orders` : "25 orders"}</dd>
              </div>
              <div>
                <dt>Fallback</dt>
                <dd>
                  {timing?.fallbackToFulfillment
                    ? `Fulfillment ${timing.afterFulfillmentDays}+ days`
                    : timing?.fallbackToOrderDate
                      ? "Order date enabled"
                      : "Delivered only"}
                </dd>
              </div>
            </dl>
            <div className="wr-policy-note">
              <PackageCheck size={16} aria-hidden />
              Non-delivered and too-recent orders remain visible only when searched, and are disabled.
            </div>
            <button className="wr-button wr-button--secondary wr-button--full" type="button" disabled={busy} onClick={() => void cancelUnsafeQueued()}>
              <ShieldCheck size={15} aria-hidden />
              Cancel unsafe queued
            </button>
          </section>
          <section className="wr-settings-host">
            <div className="wr-policy-card__header wr-settings-host__header">
              <CalendarClock size={18} aria-hidden />
              <div>
                <h2>Timing settings</h2>
                <p>Adjust the delivery delay, window, and batch limit.</p>
              </div>
            </div>
            <SettingsPanel group="Review Requests" />
          </section>
        </aside>
      </section>

      <section className="wr-panel wr-tracking-panel">
        <div className="wr-section-heading wr-section-heading--compact">
          <div>
            <h2>Request tracking board</h2>
            <p>Follow each post-purchase request from queue to customer reply.</p>
          </div>
        </div>
        <div className="wr-toolbar wr-toolbar--segmented">
          <input
            aria-label="Search requests"
            placeholder="Search order, customer, product, or status"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <select aria-label="Status" value={status} onChange={(event) => setStatus(event.target.value)}>
            {requestStatuses.map((option) => (
              <option key={option} value={option}>
                {option === "all" ? "All statuses" : labelEnum(option)}
              </option>
            ))}
          </select>
        </div>
        <AdminState
          status={requestQuery.status}
          error={requestQuery.error}
          onRetry={() => void requestQuery.reload()}
          emptyTitle="No matching requests"
          emptyText="Create a test request or adjust the filters."
        >
          <div className="wr-lifecycle-board">
            {groupedRequests.map((group) => (
              <section key={group.label} className="wr-lifecycle-column">
                <div className="wr-lifecycle-column__head">
                  <group.icon size={16} aria-hidden />
                  <span>{group.label}</span>
                  <strong>{group.rows.length}</strong>
                </div>
                <div className="wr-lifecycle-stack">
                  {group.rows.length ? (
                    group.rows.map((row) => <RequestLifecycleCard key={row.id} row={row} />)
                  ) : (
                    <div className="wr-empty-inline">No requests in this stage.</div>
                  )}
                </div>
              </section>
            ))}
          </div>
        </AdminState>
      </section>
    </div>
  );
}

function RequestMetricCard({ metric }: { metric: AdminMetric }) {
  return (
    <article className={`wr-kpi-card wr-kpi-card--${metric.tone}`}>
      <span>{metric.label}</span>
      <strong>{metric.value}</strong>
      <p>{metric.detail}</p>
    </article>
  );
}

function OrderCandidateCard({
  onToggle,
  order,
  selected,
}: {
  onToggle: () => void;
  order: AdminOrderCandidate;
  selected: boolean;
}) {
  const selectable = isSelectableOrder(order);
  const ready = order.eligible || order.manualEligible;
  return (
    <article className={`wr-order-row wr-order-row--premium${selected ? " wr-order-row--selected" : ""}${selectable ? "" : " wr-order-row--disabled"}`}>
      <label className="wr-order-check">
        <input
          type="checkbox"
          checked={selected}
          disabled={!selectable}
          onChange={onToggle}
          aria-label={`Select order ${order.orderNumber}`}
        />
      </label>
      <div className="wr-order-main">
        <div className="wr-order-title">
          <strong>{order.orderNumber}</strong>
          {order.request ? (
            <StatusBadge tone={requestTone(order.request.rawStatus)}>{order.request.status}</StatusBadge>
          ) : order.eligible ? (
            <StatusBadge tone="success">Ready</StatusBadge>
          ) : order.manualEligible ? (
            <StatusBadge tone="info">Send now</StatusBadge>
          ) : (
            <StatusBadge tone="warning">Not eligible</StatusBadge>
          )}
        </div>
        <p>{order.products}</p>
        <small>
          {order.customer} · {order.itemCount} item{order.itemCount === 1 ? "" : "s"} · {formatMoney(order.total, order.currency)}
        </small>
      </div>
      <div className="wr-order-journey" aria-label={`Review request readiness for ${order.orderNumber}`}>
        <div className="wr-journey-line">
          <span className="wr-timeline-dot wr-timeline-dot--done" />
          <span className={`wr-timeline-dot${order.deliveredAt ? " wr-timeline-dot--done" : ""}`} />
          <span className={`wr-timeline-dot${ready ? " wr-timeline-dot--ready" : ""}`} />
        </div>
        <span>Ordered {formatDate(order.processedAt)}</span>
        <small>
          {order.deliveredAt
            ? `Delivered ${formatDate(order.deliveredAt)}${order.deliveredAgeDays !== null ? ` · ${order.deliveredAgeDays}d ago` : ""}`
            : order.fulfilledAt
              ? `Fulfilled ${formatDate(order.fulfilledAt)}`
              : "No delivery signal"}
        </small>
        <em>
          {order.eligible
            ? `Ready since ${formatDate(order.readyAt ?? order.deliveredAt ?? order.processedAt)}`
            : order.manualEligible
              ? order.manualEligibilityLabel
              : order.eligibilityLabel}
        </em>
      </div>
    </article>
  );
}

function RequestLifecycleCard({ row }: { row: AdminReviewRequest }) {
  return (
    <article className="wr-request-card wr-request-card--lifecycle">
      <div className="wr-request-card__top">
        <strong>{row.order}</strong>
        <StatusBadge tone={statusTone(row.rawStatus)}>{statusLabel(row.rawStatus, row.status)}</StatusBadge>
      </div>
      <span>{row.customer}</span>
      <p>{row.products}</p>
      <div className="wr-request-meta">
        <span className="wr-channel-chip">
          <MessageCircle size={13} aria-hidden />
          {row.channel}
        </span>
        <span className="wr-channel-chip">Fallback {row.fallback}</span>
        {row.reminderCount > 0 ? <span className="wr-channel-chip">{row.reminderCount} reminders</span> : null}
      </div>
      <footer className="wr-request-footer">
        <span>Due {formatDate(row.due)}</span>
        <span>Expires {formatDate(row.expires)}</span>
      </footer>
    </article>
  );
}

function groupRequests(rows: AdminReviewRequest[]) {
  const groups = [
    { icon: AlertTriangle, label: "Needs attention", statuses: ["FAILED", "EXPIRED"] },
    { icon: Clock3, label: "Queued / pending", statuses: ["PENDING", "QUEUED", "SENT"] },
    { icon: MessageCircle, label: "Customer engaged", statuses: ["DELIVERED", "READ", "REPLIED"] },
    { icon: CheckCircle2, label: "Completed", statuses: ["SUBMITTED"] },
  ];
  return groups.map((group) => ({
    ...group,
    rows: rows.filter((row) => group.statuses.includes(row.rawStatus)),
  }));
}

function isSelectableOrder(order: AdminOrderCandidate) {
  return order.manualEligible && !order.request;
}

function labelEnum(value: string) {
  return value
    .toLowerCase()
    .split("_")
    .map((part) => `${part.slice(0, 1).toUpperCase()}${part.slice(1)}`)
    .join(" ");
}

function statusLabel(rawStatus: string, fallback: string) {
  const labels: Record<string, string> = {
    DELIVERED: "Delivered",
    EXPIRED: "Expired",
    FAILED: "Failed",
    PENDING: "Waiting",
    QUEUED: "Queued",
    READ: "Opened",
    REPLIED: "Replied",
    SENT: "Sent",
    SUBMITTED: "Review received",
  };
  return labels[rawStatus] ?? fallback;
}

function statusTone(status: string) {
  if (status === "FAILED" || status === "EXPIRED") return "critical";
  if (status === "QUEUED" || status === "PENDING") return "warning";
  if (status === "SUBMITTED" || status === "READ" || status === "REPLIED") return "success";
  if (status === "DELIVERED") return "info";
  return "info";
}

function requestTone(status: string) {
  if (status === "FAILED" || status === "EXPIRED") return "critical";
  if (status === "SUBMITTED" || status === "DELIVERED" || status === "READ" || status === "REPLIED") return "success";
  if (status === "QUEUED" || status === "PENDING" || !status) return "warning";
  return "info";
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}

function formatMoney(value: number, currency: string) {
  return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(value);
}
